// Journey Preferences service
// CRUD operations for per-trip journey preferences (transport mode, checked bags, lounge interest, etc.)
import { JOURNEY_PREFERENCES_API_BASE } from '../config/environment';
import { authFetch } from '../utils/authFetch';

/**
 * Get journey preferences for a specific trip.
 *
 * @param {string} userId - Cognito user ID
 * @param {string} tripId - Trip ID
 * @returns {Promise<Object|null>} Journey preferences or null if none exist
 */
export const getJourneyPreferences = async (userId, tripId) => {
  if (!userId || !tripId) {
    throw new Error('userId and tripId are required');
  }

  try {
    const queryParams = new URLSearchParams({ userId, tripId });
    const response = await authFetch(`${JOURNEY_PREFERENCES_API_BASE}?${queryParams}`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.preferences || null;
  } catch (error) {
    console.error('Error fetching journey preferences:', error);
    return null;
  }
};

/**
 * Save (create or update) journey preferences for a trip.
 *
 * @param {string} userId - Cognito user ID
 * @param {string} tripId - Trip ID
 * @param {Object} preferences - { transportMode, hasCheckedBags, wantsLoungeInfo, wantsGroundTransport, preferredGroundTransport }
 * @returns {Promise<Object>} Saved preferences
 */
export const saveJourneyPreferences = async (userId, tripId, preferences) => {
  if (!userId || !tripId) {
    throw new Error('userId and tripId are required');
  }

  try {
    const response = await authFetch(JOURNEY_PREFERENCES_API_BASE, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId,
        tripId,
        ...preferences,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error saving journey preferences:', error);
    throw error;
  }
};

/**
 * Get default preferences from the user's most recent trip.
 * Useful for pre-populating preferences on a new trip.
 *
 * @param {string} userId - Cognito user ID
 * @returns {Promise<Object|null>} Default preferences or null if none exist
 */
export const getDefaultPreferences = async (userId) => {
  if (!userId) {
    throw new Error('userId is required');
  }

  try {
    const queryParams = new URLSearchParams({ userId });
    const response = await authFetch(`${JOURNEY_PREFERENCES_API_BASE}/defaults?${queryParams}`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.preferences || null;
  } catch (error) {
    console.error('Error fetching default journey preferences:', error);
    return null;
  }
};

export default { getJourneyPreferences, saveJourneyPreferences, getDefaultPreferences };
